import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Phone, Search, Download, Edit, Trash2, X, Save, Layers, AlertTriangle, ChevronLeft, ChevronRight, Loader2, FileSpreadsheet } from 'lucide-react'; 
import axios from 'axios';

const Tracker = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ Separate loading states for the two buttons
  const [downloadingFiltered, setDownloadingFiltered] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // ✅ 1. INITIALIZE STATE FROM SESSION STORAGE
  const [searchTerm, setSearchTerm] = useState(() => {
      return sessionStorage.getItem('tracker_search') || '';
  });
  
  const [entriesPerPage, setEntriesPerPage] = useState(() => {
      return parseInt(sessionStorage.getItem('tracker_entries')) || 10;
  });

  const [currentPage, setCurrentPage] = useState(() => {
      return parseInt(sessionStorage.getItem('tracker_page')) || 1;
  });

  // Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [editForm, setEditForm] = useState({
      id: null,
      prescriber_name: '',
      prescriber_phone: '',
      patient_name: '',
      patient_phone: '',
      dob: '',
      insurance_provider: '', 
      latest_call_status: ''
  });

  // Change this to match your local folder path
  const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';

  useEffect(() => {
    fetchTrackerData();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('tracker_search', searchTerm);
    sessionStorage.setItem('tracker_entries', entriesPerPage);
    sessionStorage.setItem('tracker_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  const fetchTrackerData = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_tracker_list`);
      if (Array.isArray(response.data)) setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  // Helpers
  const formatID = (id) => String(id).padStart(4, '0'); 

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString.replace(' ', 'T')); 
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
           ' ' + 
           date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatSearchableDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDate = (dateString) => {
      if (!dateString || dateString === '0000-00-00') return '-';
      const datePart = dateString.split(' ')[0]; 
      const [year, month, day] = datePart.split('-');
      if (year && month && day) {
          return `${month}/${day}/${year}`;
      }
      return dateString;
  };

  const filteredData = data.filter((item) => {
    const term = searchTerm.toLowerCase();
    const dateString = item.latest_call_date ? formatSearchableDate(item.latest_call_date).toLowerCase() : '';
    
    return (
        (item.prescriber_name || '').toLowerCase().includes(term) ||
        (item.patient_name || '').toLowerCase().includes(term) ||
        (item.rx_no || '').toLowerCase().includes(term) ||
        dateString.includes(term) 
    );
  });

  const totalEntries = filteredData.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
    }
  }, [searchTerm, totalPages]);

  // ==========================================================
  // 🟢 BUTTON 1: DOWNLOAD FILTERED (Respects Search Bar)
  // ==========================================================
  // ✅ FIXED: Better Date & Universal Filtering
  // ✅ FIXED: Formats database dates (YYYY-MM-DD) to readable dates (Dec 15) before filtering
  const handleDownloadFiltered = async () => {
    setDownloadingFiltered(true);
    try {
        const response = await axios.get(`${API_URL}?action=get_tracker_export_data`);
        const fullHistory = response.data;
        console.log("DEBUG EXPORT DATA:", fullHistory);

        if (!Array.isArray(fullHistory)) { 
            alert("No data available."); 
            setDownloadingFiltered(false); 
            return; 
        }

        const term = searchTerm.toLowerCase().trim();

        // 1. If search is empty, download everything
        if (term === '') {
            generateExcel(fullHistory, "Tracker_Full_Report");
            setDownloadingFiltered(false);
            return;
        }

        // 2. Filter Logic
        const filteredExport = fullHistory.filter(item => {
            // A. Check Raw Values (e.g. searching for "Smith", "123", "Pending")
            const rawValues = Object.values(item).map(val => String(val || '').toLowerCase());
            if (rawValues.some(v => v.includes(term))) return true;

            // B. Check Formatted Date (The Fix for "Dec 15")
            // Based on your PHP, the key is likely 'date_logged', but we check variations to be safe
            const dateRaw = item.date_logged || item['Date Logged'] || item.latest_call_date || item.timestamp;

            if (dateRaw && dateRaw !== '0000-00-00') {
                const dateObj = new Date(dateRaw);
                
                // Validate date is real
                if (!isNaN(dateObj.getTime())) {
                    // Convert DB date to: "dec 15, 2025"
                    const formattedString = dateObj.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    }).toLowerCase();

                    // Check if "dec 15" matches "dec 15, 2025"
                    if (formattedString.includes(term)) return true;
                }
            }

            return false;
        });

        if (filteredExport.length === 0) { 
            alert("No records found matching your search: " + searchTerm); 
            setDownloadingFiltered(false); 
            return; 
        }

        generateExcel(filteredExport, "Tracker_Filtered_Report");

    } catch (error) { 
        console.error("Export Error:", error); 
        alert("Failed to download report."); 
    } finally { 
        setDownloadingFiltered(false); 
    }
  };

  // ==========================================================
  // 🔵 BUTTON 2: DOWNLOAD ALL (Ignores Search Bar)
  // ==========================================================
  const handleDownloadAll = async () => {
      setDownloadingAll(true);
      try {
          const response = await axios.get(`${API_URL}?action=get_tracker_export_data`);
          const fullHistory = response.data;

          if (!Array.isArray(fullHistory) || fullHistory.length === 0) { 
              alert("No data available in the database."); 
              setDownloadingAll(false); 
              return; 
          }

          // Directly download everything
          generateExcel(fullHistory, "Tracker_FULL_History");

      } catch (error) { console.error(error); alert("Failed to download."); } 
      finally { setDownloadingAll(false); }
  };

  // ✅ SHARED HELPER TO GENERATE EXCEL FILE
  const generateExcel = (data, fileNamePrefix) => {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      const wscols = [{wch: 20}, {wch: 15}, {wch: 35}, {wch: 20}, {wch: 25}, {wch: 20}, {wch: 15}, {wch: 30}];
      ws['!cols'] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, "Report");
      const fileName = `${fileNamePrefix}_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
  };

  const getStatusBadge = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s.includes('NO ANSWER')) return 'bg-red-100 text-red-700';
    if (s.includes('FOLLOW UP')) return 'bg-yellow-100 text-yellow-700';
    if (s.includes('VOICEMAIL')) return 'bg-purple-100 text-purple-700';
    if (s.includes('EMAIL') || s.includes('FAX')) return 'bg-teal-100 text-teal-700';
    return 'bg-gray-100 text-gray-600';
  };

  const handleEdit = (e, item) => {
      e.stopPropagation();
      const cleanDob = (item.dob && item.dob !== '0000-00-00') ? item.dob.split(' ')[0] : '';
      setEditForm({ 
          id: item.id, 
          prescriber_name: item.prescriber_name || '', 
          prescriber_phone: item.prescriber_phone || '', 
          patient_name: item.patient_name || '',
          patient_phone: item.patient_phone || '',
          dob: cleanDob,
          insurance_provider: item.insurance_provider || '',
          latest_call_status: item.latest_call_status || 'Pending'
      });
      setShowEditModal(true);
  };

  const handleDeleteClick = (e, item) => {
      e.stopPropagation();
      setItemToDelete(item);
      setShowDeleteModal(true);
  };

  const executeDelete = async () => {
      if (!itemToDelete) return;
      try { await axios.post(`${API_URL}?action=delete_prescription`, { id: itemToDelete.id }); fetchTrackerData(); setShowDeleteModal(false); } catch (error) { alert("Error deleting"); }
  };

  const handleUpdateSubmit = async (e) => {
      e.preventDefault();
      try { 
          await axios.post(`${API_URL}?action=update_prescriber_info`, editForm); 
          alert("Updated Successfully!"); 
          setShowEditModal(false); 
          fetchTrackerData(); 
      } catch (error) { console.error(error); alert("Error updating"); }
  };

  const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
      setCurrentPage(1); 
  };

  return (
    <div className="relative">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-3">
            <Phone className="text-teal-600" size={30} />
            Daily Call Tracker
        </h2>
        
        <div className="flex flex-wrap gap-2 items-center">
            {/* SEARCH BAR */}
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64" 
                  value={searchTerm} 
                  onChange={handleSearchChange} 
                />
            </div>
            
            {/* ✅ BUTTON 1: EXPORT FILTERED */}
            <button 
                onClick={handleDownloadFiltered} 
                disabled={downloadingFiltered || downloadingAll}
                className={`
                    px-3 py-2 rounded flex items-center gap-2 shadow-md text-white text-sm transition-all whitespace-nowrap
                    ${downloadingFiltered ? 'bg-green-400 cursor-wait' : 'bg-green-600 hover:bg-green-700'}
                `}
                title="Download only matching search results"
            >
                {downloadingFiltered ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} 
                {downloadingFiltered ? "Filtering..." : "Export Filtered"}
            </button>

            {/* ✅ BUTTON 2: DOWNLOAD ALL */}
            <button 
                onClick={handleDownloadAll} 
                disabled={downloadingFiltered || downloadingAll}
                className={`
                    px-3 py-2 rounded flex items-center gap-2 shadow-md text-white text-sm transition-all whitespace-nowrap
                    ${downloadingAll ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'}
                `}
                title="Download entire database history"
            >
                {downloadingAll ? <Loader2 className="animate-spin" size={14} /> : <FileSpreadsheet size={14} />} 
                {downloadingAll ? "Loading All..." : "Download All"}
            </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select 
            value={entriesPerPage} 
            onChange={(e) => {
                setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                setCurrentPage(1);
            }}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
            <table className="min-w-full text-xs text-left text-gray-600">
            <thead className="bg-gray-50 text-gray-700 uppercase font-bold border-b border-gray-200">
                <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Patient #</th>
                    <th className="px-4 py-3">Prescriber Name</th>
                    <th className="px-4 py-3">Prescriber Phone</th>
                    <th className="px-4 py-3">Patient Name</th>
                    <th className="px-4 py-3">Patient Phone</th>
                    <th className="px-4 py-3">DOB</th>
                    <th className="px-4 py-3">Insurance Code</th>
                    <th className="px-4 py-3 text-center font-bold text-teal-800">RX Count</th>
                    <th className="px-4 py-3 text-center">Call Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
                {loading ? (
                    <tr><td colSpan="11" className="text-center py-6">Loading tracker data...</td></tr>
                ) : paginatedData.length === 0 ? (
                    <tr><td colSpan="11" className="text-center py-10 text-gray-400 italic">No tracking records found.</td></tr>
                ) : paginatedData.map((row) => (
                    <tr 
                        key={row.id} 
                        className="hover:bg-teal-50 transition duration-150 cursor-pointer"
                        onClick={() => navigate(`/tracker/${row.id}`)}
                    >
                        <td className="px-4 py-3">
                            {row.latest_call_date ? (
                                <span className="font-bold text-gray-800 flex items-center gap-1">
                                    {formatDateTime(row.latest_call_date)}
                                </span>
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                        </td>
                        
                        <td className="px-4 py-3 font-mono text-gray-500">{formatID(row.patient_id)}</td>
                        
                        <td className="px-4 py-3 font-bold text-teal-600 hover:underline">
                            {row.prescriber_name}
                        </td>

                        <td className="px-4 py-3">{row.prescriber_phone || '-'}</td>
                        
                        <td className="px-4 py-3 font-medium text-gray-800">
                            <span 
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    navigate(`/patient/${row.patient_id}`); 
                                }}
                                className="text-teal-600 hover:text-teal-800 hover:underline cursor-pointer"
                            >
                                {row.patient_name}
                            </span>
                        </td>

                        <td className="px-4 py-3">{row.patient_phone}</td>
                        <td className="px-4 py-3">{formatDate(row.dob)}</td>
                        <td className="px-4 py-3 font-bold text-slate-600">{row.insurance_provider}</td>

                        <td className="px-4 py-3 text-center">
                            <span className="bg-teal-100 text-teal-800 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center justify-center gap-1 mx-auto w-fit">
                                <Layers size={12} /> {row.rx_count}
                            </span>
                        </td>

                        <td className="px-4 py-3 text-center">
                            {row.latest_call_status ? (
                                <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${getStatusBadge(row.latest_call_status)}`}>
                                    {row.latest_call_status}
                                </span>
                            ) : (
                                <span className="text-gray-300 text-[10px] font-bold">NO LOGS</span>
                            )}
                        </td>

                        <td className="px-4 py-3 text-center flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button onClick={(e) => handleEdit(e, row)} className="text-teal-500 hover:bg-teal-100 p-1.5 rounded"><Edit size={16}/></button>
                            <button onClick={(e) => handleDeleteClick(e, row)} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={16}/></button>
                        </td>
                    </tr>
                ))}
            </tbody>
            </table>
        </div>
      </div>

      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          
          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 border rounded ${currentPage === pageNum ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}
          </div>

          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[500px] p-6 relative">
                <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold mb-4 text-teal-700">Edit Record Details</h3>
                
                <form onSubmit={handleUpdateSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Name</label>
                            <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.prescriber_name} onChange={e => setEditForm({...editForm, prescriber_name: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Phone</label>
                            <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.prescriber_phone} onChange={e => setEditForm({...editForm, prescriber_phone: e.target.value})}/>
                        </div>
                    </div>

                    <hr className="border-gray-100 my-2"/>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Name</label>
                            <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.patient_name} onChange={e => setEditForm({...editForm, patient_name: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Phone</label>
                            <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.patient_phone} onChange={e => setEditForm({...editForm, patient_phone: e.target.value})}/>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DOB</label>
                            <input type="date" className="w-full border p-2 rounded text-sm" value={editForm.dob} onChange={e => setEditForm({...editForm, dob: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Insurance Code</label>
                            <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.insurance_provider} onChange={e => setEditForm({...editForm, insurance_provider: e.target.value})}/>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Latest Call Status</label>
                        <select 
                            className="w-full border p-2 rounded text-sm"
                            value={editForm.latest_call_status} 
                            onChange={e => setEditForm({...editForm, latest_call_status: e.target.value})}
                        >
                            <option value="Pending">Pending</option>
                            <option value="No Answer">No Answer</option>
                            <option value="Follow Up">Follow Up</option>
                            <option value="Voicemail">Voicemail</option>
                            <option value="Processing">Processing</option>
                            <option value="Fax Sent">Fax Sent</option>
                            <option value="Email Sent">Email Sent</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>

                    <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 flex items-center justify-center gap-2 mt-2">
                        <Save size={16} /> Update Details
                    </button>
                </form>
            </div>
        </div>
      )}

      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500 mt-2">
                    Are you sure you want to remove this tracker entry for <strong>{itemToDelete.prescriber_name}</strong>?
                </p>
                <div className="mt-6 flex justify-center gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button>
                    <button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default Tracker;